import type { TFunction } from "i18next";
import type { DefinitionTooltipInfo } from "src/components/common";

export const formatCompactNumber = (value: number, locale = "en"): string => {
  return new Intl.NumberFormat(locale, {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
};

export const formatPercent = (value: number, digits = 1): string =>
  `${value.toFixed(digits)}%`;

export const formatAxisPercent = (value: unknown): string =>
  `${Number(value ?? 0)}%`;

export const formatAxisCompact = (value: unknown, locale = "en"): string =>
  formatCompactNumber(Number(value ?? 0), locale);

export const formatShortDate = (value: string, locale = "en"): string => {
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
  }).format(date);
};

export const truncateLabel = (
  value: string | number | null | undefined,
  maxLength = 14,
): string => {
  const label = String(value ?? "").trim();
  if (label.length <= maxLength) {
    return label;
  }
  return `${label.slice(0, Math.max(1, maxLength - 1))}...`;
};

/** Resolve Engagement Overview DefinitionTooltip copy from en.json. */
export const getEngagementTooltipInfo = (
  t: TFunction,
  key: string,
): DefinitionTooltipInfo => {
  const base = `engagementOverview.tooltips.${key}`;
  const formula = t(`${base}.formula`, { defaultValue: "" });
  const bulletsRaw = t(`${base}.bullets`, { returnObjects: true, defaultValue: [] }) as unknown;
  const bullets = Array.isArray(bulletsRaw)
    ? bulletsRaw.filter((item): item is string => typeof item === "string" && Boolean(item.trim()))
    : undefined;

  return {
    description: t(`${base}.description`),
    formula: typeof formula === "string" && formula.trim() ? formula : undefined,
    bullets: bullets?.length ? bullets : undefined,
  };
};
